import React, { useEffect, useState, ReactElement, CSSProperties } from 'react'
import { TextField, CircularProgress } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

type Item<T> = {
  id: number | string
  label: string
  render?: ReactElement
  value: T
  displayValue?: string
}

export type TAutocompleteAsyncMui<T> = {
  asyncSearch: (query: string) => Promise<Item<T>[]>
  onSelect: (optionSelected: Item<T>) => void
  label: string
  value?: Item<T>
  textAlign?: CSSProperties['textAlign']
  disableAutocomplete?: boolean
  onClear?: () => void
  onChange?: (value) => void
  endAdornment?: JSX.Element
  errorMessage?: string
  styleTextInput?: React.CSSProperties
  disableClearable?: boolean
  hideEndAdornment?: boolean
  width?: CSSProperties['width']
  openOnFocus?: boolean
  numericOnly?: boolean
  noOptionsText?: string
}

export default function AutocompleteAsyncMui<T>({
  asyncSearch,
  onSelect,
  label,
  value,
  textAlign = 'left',
  disableAutocomplete = true,
  onClear,
  onChange,
  endAdornment,
  errorMessage,
  styleTextInput,
  disableClearable,
  hideEndAdornment,
  width = '100%',
  openOnFocus = true,
  numericOnly = false,
  noOptionsText = 'Aucun résultat',
}: TAutocompleteAsyncMui<T>): ReactElement {
  const [inputValue, setInputValue] = useState(value ?? '')
  const [options, setOptions] = useState<Item<T>[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setInputValue(value ?? '')
  }, [value])

  useEffect(() => {
    let active = true

    setLoading(true)

    asyncSearch(String(inputValue)).then((results) => {
      if (active) {
        setOptions(results)
        setLoading(false)
      }
    })

    return () => {
      active = false
    }
  }, [inputValue])

  return (
    <div style={{ width: width }}>
      <Autocomplete
        openOnFocus={openOnFocus}
        disableClearable={disableClearable}
        inputValue={typeof inputValue === 'string' ? inputValue : String(inputValue)}
        popupIcon={loading ? <CircularProgress color="inherit" size={20} /> : <ArrowDropDownIcon />}
        forcePopupIcon={!hideEndAdornment}
        value={String(value) ? { id: -1, label: String(value), value: null } : null}
        clearText="Vider"
        options={options}
        getOptionKey={(option) => option.id}
        getOptionLabel={(option) => String(option.label)}
        loading={loading}
        onChange={(_, option) => {
          if (option) {
            const newOption = (
              option && typeof option?.value === 'string' ? { ...option, value: String(option.value) } : option
            ) as Item<T>

            onSelect(newOption)
            setInputValue(option.displayValue ?? option.label)
          }
        }}
        onInputChange={(_, value, reason) => {
          switch (reason) {
            case 'clear':
              onClear?.()
              setInputValue('')
              return
            case 'input': {
              const filteredValue = numericOnly ? value.replace(/[^\d.,]/g, '').replace(/,/g, '.') : value
              setInputValue(filteredValue)
              onChange?.(filteredValue)
              break
            }
          }
        }}
        style={{ display: 'flex', flex: 1 }}
        noOptionsText={noOptionsText}
        sx={{
          '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
            color: '#b9b9b9ff',
          },
        }}
        renderOption={(props, option) => (
          <li {...props} style={{ justifyContent: textAlign === 'right' ? 'flex-end' : 'flex-start' }}>
            {option.render ?? option.label}
          </li>
        )}
        renderInput={(params) => {
          params.inputProps.autoComplete = disableAutocomplete ? 'new-password' : 'on'
          params.inputProps.name = disableAutocomplete
            ? `no-autocomplete` + Math.random()
            : typeof label === 'string'
              ? label
              : ''
          params.inputProps.autoCorrect = 'off'
          params.inputProps.autoCapitalize = 'none'
          params.inputProps.spellCheck = true

          return (
            <TextField
              {...params}
              error={!!errorMessage}
              helperText={errorMessage}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'white',
                },
                '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                  color: '#b9b9b9ff',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#092CA7',
                  fontWeight: 300,
                },
                '& .MuiInputBase-input': {
                  ...styleTextInput,
                },
                '& legend': {
                  overflow: 'unset',
                },
                '& .MuiOutlinedInput-root': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiOutlinedInput-input': {
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  textAlign,
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  textAlign: 'right',
                },
              }}
              label={label}
              //   @ts-expect-error
              slotProps={{
                input: {
                  ...params.InputProps,
                  autoComplete: disableAutocomplete ? 'new-password' : 'on',
                  name: disableAutocomplete
                    ? `no-autocomplete` + Math.random()
                    : typeof label === 'string'
                      ? label
                      : '',
                  style: {
                    display: 'flex',
                    backgroundColor: 'white',
                  },
                  endAdornment: (
                    <>
                      {endAdornment}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
                htmlInput: {
                  ...params.inputProps,
                  style: {
                    textAlign,
                  },
                  ...(numericOnly && {
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                      const allowedKeys = [
                        'Backspace',
                        'Delete',
                        'ArrowLeft',
                        'ArrowRight',
                        'ArrowUp',
                        'ArrowDown',
                        'Tab',
                        'Home',
                        'End',
                      ]
                      if (!e.ctrlKey && !e.metaKey && !/[\d.,]/.test(e.key) && !allowedKeys.includes(e.key)) {
                        e.preventDefault()
                      }
                    },
                  }),
                },
              }}
            />
          )
        }}
      />
    </div>
  )
}
